import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Task } from '@shared/models/task.model';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { Subscription, timer } from 'rxjs';
import { ListTaskService } from '@shared/modules/list/services/list-task.service';
import { TaskController } from '@shared/controllers/task.controller';
import { ConfirmationPopoverDirective } from '@shared/libs/angular-confirmation-directive/confirmation-popover.directive';
import { MatCheckboxChange } from '@angular/material/checkbox';

type TaskCheckboxChangeEvent = { value?: any; checked: boolean };

@Component({
  selector: 'task-checkbox',
  templateUrl: './task-checkbox.component.html',
  styleUrls: ['./task-checkbox.component.scss']
})
export class TaskCheckboxComponent implements OnInit {
  TaskStatus = TaskStatus;

  static readonly DELAY_AFTER_TASK_COMPLETED = 0;

  subCompletedTask: Subscription = new Subscription();

  @Input() task: Task;
  @Input() disabled: boolean = false;
  @Input() placement: string = 'right';
  @Input() disableSubtasksPopover: boolean = false;
  @Output('onCheckSubtasks') onCheckSubtasks: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output('onUpdateTask') onUpdateTask: EventEmitter<{ task: Task; subtasksCompleted?: boolean }> =
    new EventEmitter<{ task: Task; subtasksCompleted?: boolean }>();
  @ViewChild('cp') cp: ConfirmationPopoverDirective;

  protected visualChecked: boolean | null = null;
  private popoverActionTaken: boolean = false;

  constructor(private listTaskService: ListTaskService) {}

  ngOnInit(): void {}

  get checked(): boolean {
    return this.visualChecked ?? this.task?.status === TaskStatus.COMPLETED;
  }

  setVisualChecked(checked: boolean | null): void {
    this.visualChecked = checked;
  }

  setCompletedForSubtasks() {
    this.popoverActionTaken = true;
    this.visualChecked = true;
    this.onStatusChange({ value: true, checked: true }, true);
  }

  onStatusChange(e: TaskCheckboxChangeEvent, subtasksCompleted?: boolean) {
    this.subCompletedTask.unsubscribe();
    if (e.checked) {
      this.visualChecked = true;
      this.subCompletedTask = timer(TaskCheckboxComponent.DELAY_AFTER_TASK_COMPLETED).subscribe(() => {
        if (subtasksCompleted) {
          this.completeSubtasks();
          this.onCheckSubtasks.emit(true);
        }
        this.task.status = TaskStatus.COMPLETED;
        this.onUpdateTask.emit({ task: this.task, subtasksCompleted });
        this.visualChecked = null;
        this.subCompletedTask.unsubscribe();
      });
    } else {
      this.task.status = TaskStatus.CREATED;
      this.listTaskService.updateTask(this.task);
      this.onUpdateTask.emit({ task: this.task, subtasksCompleted });
      this.visualChecked = null;
    }
  }

  completeOnlyCurrentTask() {
    this.popoverActionTaken = true;
    this.visualChecked = true;
    this.onStatusChange({ value: true, checked: true }, false);
  }

  onCheckboxWithSubtasksClick(e: MouseEvent) {
    if (this.disableSubtasksPopover) {
      return;
    }

    e.preventDefault();
    this.popoverActionTaken = false;
    this.visualChecked = true;
  }

  onPopoverOpenChange(isOpen: boolean) {
    if (isOpen) {
      return;
    }

    if (!this.popoverActionTaken && this.task?.status !== TaskStatus.COMPLETED) {
      this.visualChecked = null;
    }
  }

  onDefaultCheckboxChange(e: MatCheckboxChange) {
    this.onStatusChange({ checked: e.checked, value: e.source.value });
  }

  completeSubtasks() {
    const complete = (task) => {
      task.children.map((t: Task) => {
        t.status = TaskStatus.COMPLETED;
        this.listTaskService.updateTask(t);
        complete(t);
        return t;
      });
    };
    complete(this.task);
    const ctrl = new TaskController(this.task);
    ctrl.completeSubtasks(this.task).toPromise();
  }
}
